import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Publish as PublishIcon,
  Unpublished as UnpublishIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

const CATEGORIES = [
  'Travel Guidelines',
  'HR Policies',
  'Work From Home',
  'Leave Policy',
  'IT & Security',
  'Finance & Expense',
  'Code of Conduct',
  'General Guidelines',
];

const EMPTY_FORM = {
  title: '',
  category: '',
  description: '',
  version: '1.0',
  effectiveDate: '',
};

const AdminPolicyManagement = () => {
  const { tokenManager } = useAuth();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);

  const [previewPolicy, setPreviewPolicy] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${tokenManager.getToken()}` }),
    [tokenManager]
  );

  const fetchPolicies = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (categoryFilter) params.append('category', categoryFilter);

      const response = await fetch(`${API_URL}/policies/admin/all?${params}`, {
        headers: authHeaders(),
      });

      if (response.ok) {
        const result = await response.json();
        setPolicies(result.policies || []);
      } else {
        toast.error('Failed to fetch policies');
      }
    } catch (error) {
      console.error('Error fetching policies:', error);
      toast.error('Failed to fetch policies');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, statusFilter, categoryFilter]);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  // Release the object URL when the preview closes or switches document
  useEffect(() => {
    return () => {
      if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleOpenCreate = () => {
    setEditingPolicy(null);
    setFormData(EMPTY_FORM);
    setFile(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (policy) => {
    setEditingPolicy(policy);
    setFormData({
      title: policy.title,
      category: policy.category,
      description: policy.description || '',
      version: policy.version || '1.0',
      effectiveDate: policy.effectiveDate ? policy.effectiveDate.substring(0, 10) : '',
    });
    setFile(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPolicy(null);
    setFormData(EMPTY_FORM);
    setFile(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (publishImmediately = false) => {
    if (!formData.title.trim()) {
      toast.error('Please enter a policy title');
      return;
    }
    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }
    if (!editingPolicy && !file) {
      toast.error('Please attach a policy document');
      return;
    }

    setSaving(true);
    try {
      if (editingPolicy) {
        // Metadata first, then the file only if the admin picked a new one
        const metaResponse = await fetch(`${API_URL}/policies/${editingPolicy.policyId}`, {
          method: 'PUT',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });

        if (!metaResponse.ok) {
          const result = await metaResponse.json();
          toast.error(result.details || result.error || 'Failed to update policy');
          return;
        }

        if (file) {
          const fileData = new FormData();
          fileData.append('file', file);
          fileData.append('version', formData.version);

          const fileResponse = await fetch(`${API_URL}/policies/${editingPolicy.policyId}/file`, {
            method: 'PUT',
            headers: authHeaders(),
            body: fileData,
          });

          if (!fileResponse.ok) {
            const result = await fileResponse.json();
            toast.error(result.details || result.error || 'Policy saved, but the document failed to upload');
            return;
          }
        }

        toast.success('Policy updated successfully');
      } else {
        const uploadData = new FormData();
        uploadData.append('file', file);
        Object.entries(formData).forEach(([key, value]) => uploadData.append(key, value));
        uploadData.append('status', publishImmediately ? 'published' : 'draft');

        const response = await fetch(`${API_URL}/policies`, {
          method: 'POST',
          headers: authHeaders(),
          body: uploadData,
        });

        if (!response.ok) {
          const result = await response.json();
          toast.error(result.details || result.error || 'Failed to create policy');
          return;
        }

        toast.success(publishImmediately ? 'Policy created and published' : 'Policy saved as draft');
      }

      handleCloseDialog();
      fetchPolicies();
    } catch (error) {
      console.error('Error saving policy:', error);
      toast.error('Failed to save policy');
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async (policy) => {
    const nextStatus = policy.status === 'published' ? 'draft' : 'published';

    try {
      const response = await fetch(`${API_URL}/policies/${policy.policyId}/publish`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: nextStatus }),
      });

      if (response.ok) {
        toast.success(
          nextStatus === 'published'
            ? 'Policy published — it is now visible to all employees'
            : 'Policy unpublished — it is hidden from employees'
        );
        fetchPolicies();
      } else {
        const result = await response.json();
        toast.error(result.error || 'Failed to update publish status');
      }
    } catch (error) {
      console.error('Error updating publish status:', error);
      toast.error('Failed to update publish status');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      const response = await fetch(`${API_URL}/policies/${deleteTarget.policyId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });

      if (response.ok) {
        toast.success('Policy deleted successfully');
        fetchPolicies();
      } else {
        const result = await response.json();
        toast.error(result.error || 'Failed to delete policy');
      }
    } catch (error) {
      console.error('Error deleting policy:', error);
      toast.error('Failed to delete policy');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handlePreview = async (policy) => {
    setPreviewPolicy(policy);
    setPreviewLoading(true);

    try {
      const response = await fetch(`${API_URL}/policies/${policy.policyId}/view`, {
        headers: authHeaders(),
      });

      if (!response.ok) {
        toast.error('Failed to load policy document');
        setPreviewPolicy(null);
        return;
      }

      const blob = await response.blob();
      setPreviewUrl(window.URL.createObjectURL(blob));
    } catch (error) {
      console.error('Error loading preview:', error);
      toast.error('Failed to load policy document');
      setPreviewPolicy(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewPolicy(null);
  };

  const handleDownload = async (policy) => {
    try {
      const response = await fetch(`${API_URL}/policies/${policy.policyId}/download`, {
        headers: authHeaders(),
      });

      if (!response.ok) {
        toast.error('Download failed');
        return;
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition');
      let downloadName = policy.title;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="([^"]+)"/);
        if (match && match[1]) downloadName = match[1];
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        if (document.body.contains(link)) document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 500);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download policy');
    }
  };

  const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Container maxWidth={false} disableGutters sx={{ width: '100%' }}>
        <Box sx={{ mb: 4, mt: 2 }}>
          <Typography gutterBottom sx={{ fontWeight: 600, color: 'text.primary', letterSpacing: 1, fontSize: '30px' }}>
            Policy Management
          </Typography>
          <Typography variant="h5" color="text.secondary" gutterBottom sx={{ fontStyle: 'italic', fontWeight: 400, fontSize: '20px' }}>
            Create, update and publish company policies for employees
          </Typography>
        </Box>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select value={categoryFilter} label="Category" onChange={(e) => setCategoryFilter(e.target.value)}>
                  <MenuItem value="">All Categories</MenuItem>
                  {CATEGORIES.map((category) => (
                    <MenuItem key={category} value={category}>{category}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: 'flex', gap: 1, justifyContent: { sm: 'flex-end' } }}>
              <Button startIcon={<RefreshIcon />} onClick={fetchPolicies} variant="outlined">
                Refresh
              </Button>
              <Button startIcon={<AddIcon />} onClick={handleOpenCreate} variant="contained">
                New Policy
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Paper>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : policies.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="info">
                No policies yet. Click <strong>New Policy</strong> to create your first one.
              </Alert>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#fafafa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Policy</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Version</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Effective</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Last Updated</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {policies.map((policy) => (
                    <TableRow key={policy.policyId} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{policy.title}</Typography>
                        {policy.description && (
                          <Typography variant="caption" color="text.secondary">
                            {policy.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={policy.category} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{policy.version}</TableCell>
                      <TableCell>{formatDate(policy.effectiveDate)}</TableCell>
                      <TableCell>{formatDate(policy.updatedAt)}</TableCell>
                      <TableCell>
                        <Chip
                          label={policy.status === 'published' ? 'Published' : 'Draft'}
                          color={policy.status === 'published' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Tooltip title="Preview">
                            <IconButton size="small" color="primary" onClick={() => handlePreview(policy)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download">
                            <IconButton size="small" color="primary" onClick={() => handleDownload(policy)}>
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" color="primary" onClick={() => handleOpenEdit(policy)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={policy.status === 'published' ? 'Unpublish' : 'Publish'}>
                            <IconButton
                              size="small"
                              color={policy.status === 'published' ? 'warning' : 'success'}
                              onClick={() => handleTogglePublish(policy)}
                            >
                              {policy.status === 'published'
                                ? <UnpublishIcon fontSize="small" />
                                : <PublishIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => setDeleteTarget(policy)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {editingPolicy ? 'Edit Policy' : 'New Policy'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Policy Title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="e.g. Work From Home Policy"
              />
            </Grid>
            <Grid item xs={12} sm={7}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select name="category" value={formData.category} label="Category" onChange={handleInputChange}>
                  {CATEGORIES.map((category) => (
                    <MenuItem key={category} value={category}>{category}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Version"
                name="version"
                value={formData.version}
                onChange={handleInputChange}
                placeholder="1.0"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Effective Date"
                name="effectiveDate"
                type="date"
                value={formData.effectiveDate}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Short summary shown to employees"
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} fullWidth>
                {file ? file.name : editingPolicy ? 'Replace Document (optional)' : 'Attach Document'}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp"
                  onChange={(e) => setFile(e.target.files[0] || null)}
                />
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                PDF, Word, Excel or image, up to 25MB. PDFs and images preview in the browser;
                Word and Excel files are download-only for employees.
              </Typography>
              {editingPolicy && !file && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Current document: {editingPolicy.originalName || editingPolicy.fileName}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} disabled={saving}>Cancel</Button>
          {editingPolicy ? (
            <Button variant="contained" onClick={() => handleSave()} disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          ) : (
            <>
              <Button variant="outlined" onClick={() => handleSave(false)} disabled={saving}>
                Save as Draft
              </Button>
              <Button variant="contained" onClick={() => handleSave(true)} disabled={saving}>
                {saving ? 'Saving…' : 'Save & Publish'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Preview dialog */}
      <Dialog open={!!previewPolicy} onClose={handleClosePreview} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, pr: 6 }}>
          {previewPolicy?.title}
          <IconButton onClick={handleClosePreview} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {previewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : previewPolicy && previewUrl ? (
            previewPolicy.isImage ? (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <img
                  src={previewUrl}
                  alt={previewPolicy.title}
                  style={{ maxWidth: '100%', maxHeight: '600px', objectFit: 'contain' }}
                />
              </Box>
            ) : previewPolicy.isPDF ? (
              <iframe src={previewUrl} width="100%" height="600px" title={previewPolicy.title} style={{ border: 'none' }} />
            ) : (
              <Alert severity="info">
                This file type cannot be previewed in the browser. Use Download to open it.
              </Alert>
            )
          ) : null}
        </DialogContent>
        <DialogActions>
          {previewPolicy && (
            <Button startIcon={<DownloadIcon />} onClick={() => handleDownload(previewPolicy)}>
              Download
            </Button>
          )}
          <Button onClick={handleClosePreview}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Delete Policy</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete <strong>{deleteTarget?.title}</strong>? This removes the policy and its document
            permanently, and it will disappear for all employees.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPolicyManagement;
